import { useState, useEffect, useCallback } from 'react';
import './App.css';

// Hidden admin dashboard, reached at /#admin. Gated by ADMIN_TOKEN (entered once,
// kept in localStorage). Shows the moderation queue and applies actions.

const TOKEN_KEY = 'uhcs_admin_token';

function ServerRow({ item, actions, onAction }) {
  return (
    <div className="admin-item">
      <div className="admin-item-main">
        <span className="admin-item-title">
          COSC {item.courseId}
          {item.courseName ? ` · ${item.courseName}` : ''} · {item.professorName}
        </span>
        <span className="admin-item-sub">
          {item.guildName || item.inviteCode}
          {item.reportCount > 0 && ` · ${item.reportCount} report${item.reportCount === 1 ? '' : 's'}`}
          {Array.isArray(item.reasons) && item.reasons.length > 0 &&
            ` (${item.reasons.map((r) => `${r.n} ${r.reason}`).join(', ')})`}
        </span>
        <a
          className="admin-item-link"
          href={item.inviteUrl || `https://discord.gg/${item.inviteCode}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {item.inviteUrl || `discord.gg/${item.inviteCode}`}
        </a>
      </div>
      <div className="admin-actions">
        {actions.map((a) => (
          <button
            key={a.action}
            type="button"
            className={`admin-btn admin-btn-${a.kind || 'default'}`}
            onClick={() => onAction(item.id, a.action)}
          >
            {a.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function Section({ title, items, actions, onAction, empty }) {
  return (
    <section className="admin-section">
      <h2>
        {title} <span className="admin-count">{items.length}</span>
      </h2>
      {items.length === 0 ? (
        <p className="admin-empty">{empty}</p>
      ) : (
        items.map((item) => (
          <ServerRow key={item.id} item={item} actions={actions} onAction={onAction} />
        ))
      )}
    </section>
  );
}

export default function AdminPanel() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const [tokenInput, setTokenInput] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (tok) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/admin/queue', {
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (res.status === 401) {
        setError('Invalid admin token.');
        setData(null);
        return;
      }
      if (!res.ok) throw new Error('load failed');
      setData(await res.json());
    } catch {
      setError('Could not load the queue. Run `vercel dev` locally, or use the deployed site.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const saveToken = (e) => {
    e.preventDefault();
    localStorage.setItem(TOKEN_KEY, tokenInput.trim());
    setToken(tokenInput.trim());
  };

  const signOut = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    setData(null);
  };

  const onAction = async (serverId, action) => {
    try {
      const res = await fetch('/api/admin/moderate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ serverId, action }),
      });
      if (res.status === 401) setError('Invalid admin token.');
      else if (res.ok) load(token);
    } catch {
      /* ignore */
    }
  };

  if (!token) {
    return (
      <div className="admin-panel">
        <h1>Admin</h1>
        <form className="admin-token-form" onSubmit={saveToken}>
          <input
            type="password"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            placeholder="Admin token"
            autoFocus
          />
          <button type="submit" className="cta-button">Enter</button>
        </form>
        {error && <p className="add-server-error">{error}</p>}
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h1>Moderation queue</h1>
        <div>
          <button type="button" className="admin-btn" onClick={() => load(token)}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
          <button type="button" className="admin-btn" onClick={signOut}>Sign out</button>
        </div>
      </div>

      {error && <p className="add-server-error">{error}</p>}

      {data && (
        <>
          <Section
            title="Pending submissions"
            items={data.pending}
            empty="Nothing waiting for review."
            onAction={onAction}
            actions={[
              { action: 'approve', label: 'Approve', kind: 'good' },
              { action: 'reject', label: 'Reject', kind: 'bad' },
            ]}
          />
          <Section
            title="Reported (still live)"
            items={data.reported}
            empty="No reported links."
            onAction={onAction}
            actions={[
              { action: 'dismiss', label: 'Dismiss reports', kind: 'good' },
              { action: 'remove', label: 'Remove', kind: 'bad' },
            ]}
          />
          <Section
            title="Auto-removed"
            items={data.removed}
            empty="Nothing has been auto-removed."
            onAction={onAction}
            actions={[{ action: 'restore', label: 'Restore', kind: 'good' }]}
          />
        </>
      )}
    </div>
  );
}
